import axios from 'axios';
import { TicketListItem, TicketDetail, ReanalysisRequest, IngestResponse, HealthResponse, ProcessContext, PiiIgnoreEntry, ActiveDistribution, ScaffoldSegment, DraftKind, SenderOrganisationRef, DeclineInstead } from '../types';
import { getAuthHeader, onAuthRejected, beginInteractiveLogin } from '../auth/identity';
import { formatBackendError } from '../utils/formatError';
import { readCachedVocabConfig, writeConfigCache, type CachedVocabConfig } from './configCache';
// The one reading of a lost suggestion race — this client flags the envelope, every surface above
// reads that flag through the same predicate (services/staleSuggestion.ts).
import { isStaleSuggestionError } from './staleSuggestion';
import type { ActiveBucket, ArchiveBucket, LifecycleBucket } from './lifecycleBuckets';
export type { CachedVocabConfig } from './configCache';
// Re-export so existing call sites can keep importing from services/api.
// Function bodies live in utils/formatError.ts so test files that auto-mock
// '../services/api' don't accidentally null them out.
export { formatBackendError, mapBackendError } from '../utils/formatError';

// <purpose>All API calls to the smarttix.core Function App (per-customer Bicep deployment).</purpose>
// <dependencies>getAuthHeader (auth/identity.ts) — Authorization: Bearer for cross-origin calls.</dependencies>
//
// Local dev: CRA proxy (setupProxy.js) routes /api → localhost:7071 (same-origin, no CORS).
// Production: REACT_APP_CORE_API_URL set at build time to the customer's FA (cross-origin).
const CORE_API_URL = process.env.REACT_APP_CORE_API_URL || '/api';

const coreHttp = axios.create({ baseURL: CORE_API_URL });
coreHttp.interceptors.request.use(async (config) => {
  const auth = await getAuthHeader();
  if (auth) config.headers[auth.name] = auth.value;
  return config;
});

// Why: backend sends camelCase via WorkerOptions.Serializer, but this safety net
// catches any residual PascalCase keys from WriteStringAsync paths.
const toCamelCase = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
// Why: skip keys that are domain identifiers (K0, K3A, reason codes like beschwerde) — only camelize PascalCase property names
const shouldCamelize = (s: string): boolean => s.length > 1 && s.charAt(0) >= 'A' && s.charAt(0) <= 'Z' && s.charAt(1) >= 'a' && s.charAt(1) <= 'z';
const camelizeKeys = (obj: unknown): unknown => {
  if (Array.isArray(obj)) return obj.map(camelizeKeys);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Blob)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [shouldCamelize(k) ? toCamelCase(k) : k, camelizeKeys(v)])
    );
  }
  return obj;
};
coreHttp.interceptors.response.use((response) => {
  if (response.config.responseType === 'blob') return response;
  if (response.data && typeof response.data === 'object') {
    response.data = camelizeKeys(response.data);
  }
  return response;
}, (error) => {
  // Why: a server-rejected token → backend 401 → empty dashboard instead of a login prompt.
  // Invalidate the token cache, then begin interactive login THROUGH the facade — it preserves
  // the current URL as the post-login return and owns the one door. Never a hard-coded href
  // here: a login link that bypasses the facade never feeds the MSAL cache.
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    onAuthRejected();
    void beginInteractiveLogin();
  }
  return Promise.reject(error);
});


// ─── Triage draft wire constants (Part C, 2026-09-07) ───
// This file is the adjudicated home of the triage pipeline's wire spellings (FE role-literal
// ratchet, services/__tests__/roleFlavouringScan.test.ts — two ledger rows for services/api.ts).
// Both are written out plainly, so each is greppable as the string that actually goes on the wire,
// and each is spelled ONCE — every call site imports the constant instead of re-typing the value,
// which is how a route or tag rename stays a one-line change.
// - TRIAGE_ACTIONS_ROUTE: the actions route every triage call builds on.
// - RENDER_SOURCE_TRIAGE_DRAFT: the render_source the triage composer stamps on a draft it
//   produced; services/draftClaims.ts re-exports it so draft surfaces read one vocabulary module
//   rather than the API client.
export const TRIAGE_ACTIONS_ROUTE = '/manage/wim/actions';
export const RENDER_SOURCE_TRIAGE_DRAFT = 'wim-draft-v1';

export type ClaimVia = 'approve' | 'copy';

export interface TriageActionResult {
  success: boolean;
  resultState?: string;
  error?: string;
  // The suggestion row moved on under the clerk — the remedy is a reload, not a retry.
  stale?: boolean;
}

export interface EditSuggestionResult {
  success: boolean;
  error?: string;
  // Same fact as TriageActionResult.stale, reached over a different endpoint: the scaffold fill was
  // confirmed against a suggestion row that is no longer the current one.
  stale?: boolean;
}

// The answer to a draft-slot rewrite (Part C wave 2, 2026-09-09). Deliberately the SAME shape as
// EditSuggestionResult rather than a richer one: success carries nothing (204 No Content — the new
// text and the new stamp arrive through the caller's refetch), and the only fact worth flagging
// beside the sentence is whether the row moved on, because that is the one outcome whose remedy is
// a reload rather than something the clerk reads.
export interface EditDraftSlotResult {
  success: boolean;
  error?: string;
  stale?: boolean;
}

export interface RenderSecondaryDraftResult {
  success: boolean;
  text?: string;
  // The composition's own provenance and letter, as the ANSWER carried them (2026-09-17). Read by
  // the surface that reveals the composed card from this response rather than from a refetch —
  // which the stateless receipt makes mandatory: there is no draft row for a refetch to bring.
  // Absent on a core that answers with the text alone; the reveal then shows the text alone.
  renderSource?: string;
  scaffoldJson?: string | null;
  error?: string;
  stale?: boolean;
  // The composer had nothing to produce — an outcome to explain, never a generic failure.
  refused?: boolean;
}

export interface UserRoles {
  email: string;
  isNormalUser: boolean;
  isCustomerAdmin: boolean;
  isAdmin: boolean;
}

export interface UserEntry {
  id: number;
  email: string;
  isNormalUser: boolean;
  isCustomerAdmin: boolean;
  isAdmin: boolean;
}

export interface RejectionFlowItem {
  fromPrimaryAxis: string;
  toPrimaryAxis: string;
  count: number;
}

export interface DailyTicketCount {
  ticketDate: string;
  count: number;
}

// Phase 4B rename (spec §6 naming table): class renamed outright (not itself wire — only its
// `.primaryAxis` property rides the wire, dual-served with the pre-rename `.category` shim during
// the expand window; see normalizePrimaryAxisVolumeBin below).
export interface PrimaryAxisVolumeBin {
  binLabel: string;
  binStart: string;
  primaryAxis: string;
  count: number;
}

// One time bin's residue (part 7 round 5, 2026-10-14): the bin's failed tickets and its tickets without a real
// category (the page's class, as totalWithoutPrimaryAxisCount) — same bin grammar as PrimaryAxisVolumeBin.
export interface PeriodResidueBin {
  binStart: string;
  binLabel: string;
  failed: number;
  withoutPrimaryAxis: number;
}

// Per-bucket reanalysis retry distribution (TicketStatisticsDto.RetryBuckets, camelCased).
export interface PrimaryAxisRetryBuckets {
  zero: number;
  one: number;
  two: number;
  three: number;
  fourPlus: number;
}

export interface TicketStatistics {
  totalReceived: number;
  totalUnprocessed: number;
  totalOffen: number;
  totalApproved: number;
  totalCopyApproved?: number;
  totalArchived: number;
  archivedUnprocessed: number;
  archivedOffen: number;
  archivedApproved: number;
  // How many of the Erledigt rows are there by the „Nicht hilfreich" verdict (core 2026-10-16) —
  // the Erledigt card's own composition on the archive lens. Optional: an older backend omits it.
  archivedDeclinedCount?: number;
  // The declined rows still ACTIVE in Entschieden (Genehmigen settles the row, 2026-10-23) — the
  // active-lens twin of archivedDeclinedCount; totalApproved counts approvals alone. Optional.
  totalDeclined?: number;
  avgProcessingSeconds: number | null;
  perPrimaryAxisCount: Record<string, number>;
  perSecondaryAxisCount: Record<string, number>;
  rejectionFlow: RejectionFlowItem[];
  dailyVolume: DailyTicketCount[];
  // Phase 4+: aggregated volume bins (kw/q). Present when aggregation != 'daily'.
  perPrimaryAxisVolumeByPeriod?: PrimaryAxisVolumeBin[];
  // Phase 5+: additional KPI fields from BE v2 response.
  reanalyseVerlauf?: Record<string, { total: number; approved: number }>;
  perPrimaryAxisA0Rate?: number | null;
  perPrimaryAxisA2Rate?: number | null;
  perPrimaryAxisOffen?: Record<string, number>;
  perPrimaryAxisErfolgreich?: Record<string, number>;
  // These four fields have no FE reader today (server-mirrored, contraction-ready wire fields —
  // served now so a future stats surface can consume them without a BE round-trip; not dead code,
  // just unbuilt UI).
  perPrimaryAxisRetryBuckets?: Record<string, PrimaryAxisRetryBuckets>;
  // Per-axis BASELINE_K* values loaded from customer_config. Used by the PDF export's
  // Abnahmekriterien column.
  perPrimaryAxisBaselines?: Record<string, number>;
  avgClassifierConfidence?: number | null;
  highClassifierConfidenceRate?: number | null;
  archivedFehlgeschlagenCount?: number;
  totalFehlgeschlagenCount?: number;
  archivedUnclassifiedCount?: number;
  // Optional, additive: archived tickets in the Ungeklärt card's OWN sub-reason "unclassified" (archived, bucket
  // Ungeklärt, classifier gave no category) — the Ungeklärt KPI sub-line reads THIS, never archivedUnclassifiedCount
  // (the export's class), so the card's breakdown can never exceed the card. Absent on older backends: stays undefined.
  archivedUngeklaertUnclassifiedCount?: number;
  totalUnclassifiedCount?: number;
  // Optional, additive: every window ticket that is NOT failed and has no real category (the classifier's
  // unclassified value OR never classified) — the PAGE's class (the Volumen je Kategorie residue column, the donut's „+n"). Distinct from
  // totalUnclassifiedCount (the classifier said KU), the export's class. Absent on older backends: stays undefined.
  totalWithoutPrimaryAxisCount?: number;
  // Optional, additive: the residue per time bin, matched to perPrimaryAxisVolumeByPeriod by binLabel — the volume bars'
  // small „+n" line. Absent on older backends: stays undefined (no „+n" anywhere; never derived).
  perPeriodResidue?: PeriodResidueBin[];
  headlineRateMode?: string;
  requestedAggregation?: string;
  // Optional, additive (the Statistik third row, 2026-10-28): per bucket (the served `bucket` face
  // key) the small facts under its card, child key -> count, in SLOT ORDER, every child served,
  // zeros included. The page mounts a child that counts in either lens of this fetch
  // (StatistikSeite/bucketChildren.ts). Absent on older backends: two rows, nothing derived.
  bucketChildren?: Partial<Record<LifecycleBucket, Record<string, number>>>;
}

// ═══════════════════════════════════════════════════════════
// Phase 4B expand-window boundary coalesce (spec §6, task F1)
// ═══════════════════════════════════════════════════════════
// Expand-window tolerance, the FE's only knowledge of the legacy wire names: a pre-expansion BE
// serves only the old pair, an expanded BE serves both — coalesce once here at the parse
// boundary, deleted at the contraction (see smarttix-core.md § Contraction discipline). No
// component below this line may mention the legacy names.

const normalizeTicket = <T extends {
  primaryAxis?: string; category?: string;
  classifierConfidence?: number; categoryConfidence?: number;
  secondaryAxis?: string[]; reasonCodes?: string[];
}>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
  classifierConfidence: raw.classifierConfidence ?? raw.categoryConfidence,
  secondaryAxis: raw.secondaryAxis ?? raw.reasonCodes,
});

const normalizeSuggestion = <T extends {
  primaryAxis?: string; category?: string;
  suggestionConfidence?: number; confidence?: number;
}>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
  suggestionConfidence: raw.suggestionConfidence ?? raw.confidence,
} as T);

const normalizeRejectionFlowItem = <T extends {
  fromPrimaryAxis?: string; fromCategory?: string;
  toPrimaryAxis?: string; toCategory?: string;
}>(raw: T): T => ({
  ...raw,
  fromPrimaryAxis: raw.fromPrimaryAxis ?? raw.fromCategory,
  toPrimaryAxis: raw.toPrimaryAxis ?? raw.toCategory,
} as T);

const normalizePrimaryAxisVolumeBin = <T extends { primaryAxis?: string; category?: string }>(raw: T): T => ({
  ...raw,
  primaryAxis: raw.primaryAxis ?? raw.category,
});

// Statistics carries the widest fan-out of renamed fields (spec §6 E3 list) — one coalesce home
// covering every sub-object the FE consumes, applied once at getStatistics' parse boundary.
const normalizeStatistics = (raw: any): TicketStatistics => ({
  ...raw,
  perPrimaryAxisCount: raw.perPrimaryAxisCount ?? raw.categories,
  perSecondaryAxisCount: raw.perSecondaryAxisCount ?? raw.reasonCodes,
  rejectionFlow: raw.rejectionFlow?.map(normalizeRejectionFlowItem),
  perPrimaryAxisVolumeByPeriod: (raw.perPrimaryAxisVolumeByPeriod ?? raw.categoryVolumeByPeriod)?.map(normalizePrimaryAxisVolumeBin),
  perPrimaryAxisA0Rate: raw.perPrimaryAxisA0Rate ?? raw.categoryA0Rate,
  perPrimaryAxisA2Rate: raw.perPrimaryAxisA2Rate ?? raw.categoryA2Rate,
  perPrimaryAxisOffen: raw.perPrimaryAxisOffen ?? raw.categoryOffen,
  perPrimaryAxisErfolgreich: raw.perPrimaryAxisErfolgreich ?? raw.categoryErfolgreich,
  perPrimaryAxisRetryBuckets: raw.perPrimaryAxisRetryBuckets ?? raw.categoryRetryDistribution,
  perPrimaryAxisBaselines: raw.perPrimaryAxisBaselines ?? raw.categoryBaselines,
  avgClassifierConfidence: raw.avgClassifierConfidence ?? raw.avgCategoryConfidence,
  highClassifierConfidenceRate: raw.highClassifierConfidenceRate ?? raw.highConfidenceRate,
  archivedUnclassifiedCount: raw.archivedUnclassifiedCount ?? raw.archivedUnklarCount,
  totalUnclassifiedCount: raw.totalUnclassifiedCount ?? raw.totalUnklarCount,
});

// UI_WARP_APPROVE_TO_GENEHMIGT is DELETED (Genehmigen settles the row, 2026-10-23): after the press
// the pane stays on the row on every tenant; no setting decides that.
export interface UiWarps {
  processToAntworten: boolean;
  testgenProcessToAntworten: boolean;
  testgenUploadToUploads: boolean;
}

// One value on a classification axis. `id`/`label` are the two the wire has always carried; the
// optional fields below mirror core's `SchemaValuePayload` EXACTLY, including its
// omit-when-null serialization — a value that carries none of them arrives with none of the keys,
// which is what makes them additive: a backend predating them, and every axis that has no use for
// them (wim's process-type axis carries no presentation trio), deserialize identically to before.
// - abbr: the value's own COMPACT SPELLING, for the surfaces that render a code rather than a word
//   (the colour strip, the legend, the second-axis pill). Served rather than derived: this
//   frontend used to truncate the id to three characters, which is not an
//   abbreviation anyone decided and cannot be re-worded without a deploy. Read it through
//   useCategoryMeta().abbr — the fallback tier below it (the served label, then the truncation)
//   has one home, in categoryMeta.resolveAbbr, and re-implementing it at a call site is how a role
//   starts rendering a spelling nobody chose.
// - minCategory/maxCategory: the primary-axis band this SECOND-axis value is valid for (the served
//   form of the baked REASON_CODES min/max pair). Consumed from Task 9 onward; typed here because
//   the wire already carries them and a field the FE cannot see is a field the FE cannot honour.
// - excludedFromEvaluation: THE marker. The tenant declaring "this value is the unclassified bucket, not a
//   classification outcome" — the served replacement for the frontend knowing that gMSB's unclassified id is
//   spelled 'KU'. Read it via useCategoryMeta().performanceCategoryKeys, never by re-filtering
//   here: the split between reconciliation and categorization-performance enumeration has exactly
//   one home (ClassificationSchemaContext).
// - color/icon/hint: THE PRESENTATION TRIO (wim vocabulary v7, 2026-09-13). A value's hue, its
//   glyph name and its one-sentence hover prose, served by the tenant instead of baked into this
//   bundle — the same move `abbr` already made for the compact spelling, and for the same reason:
//   re-wording or re-hueing a value must be a data edit, not a deploy. `color` is `#RRGGBB`;
//   `icon` is a NAME from the agreed glyph set (forward | reply | lookup | archive | question),
//   never markup, so the renderer keeps control of size, stroke and a11y; `hint` is the German
//   sentence that says what the value MEANS, which the tooltip pairs with the label. Read all
//   three through useCategoryMeta().color/.icon/.hint — the fallback tiers below them live in
//   categoryMeta.ts, and a call site that reaches into theme.ts for a palette directly is how a
//   tenant's own palette stops reaching one surface.
export interface SchemaValue {
  id: string;
  label: string;
  abbr?: string;
  color?: string;
  icon?: string;
  hint?: string;
  minCategory?: string;
  maxCategory?: string;
  excludedFromEvaluation?: boolean;
}

// One CLASSIFIED-ALONGSIDE attribute the tenant declares (wim vocabulary v7, 2026-09-13): the
// per-ticket facts that are NOT one of the two axes — the reminder flag, the commodity,
// the sender's kind. Served as a LIST rather than a fixed set of fields so a tenant can add one
// without a frontend change, which is the whole point.
// - `type` discriminates what the value space is ('scalar' | 'boolean' | 'enum' today); only an
//   enum carries `values`/`faces`, and an unknown type must render nothing rather than guess.
// - `labelDe` is the German noun for the attribute itself (a filter section's heading).
// - `faces` is the DISPLAY DECISION per value, and its three states are all meaningful:
//   a string = render this word; an explicit `null` = render NO pill (the unremarkable default a
//   surface would only add noise by repeating); an ABSENT key = no decision was served, so the
//   propercased value stands in. Null and absent must therefore stay distinguishable — read the
//   pair through useCategoryMeta().attributeFace, never by indexing `faces` at a call site.
export interface VocabularyAttribute {
  name: string;
  type: string;
  labelDe?: string;
  values?: string[];
  faces?: Record<string, string | null>;
}
export interface VnbSlot { id: string; label: string; source: 'mail' | 'system' | 'hybrid'; }
export interface ClassificationSchema { marktprozess: SchemaValue[]; anliegen: SchemaValue[]; slots: VnbSlot[]; }

// One classification axis as DATA: the schema's own axis id, the German UI noun for it, and its
// values. This is what lets the FE render categories without knowing a role name — a new
// schema-driven role needs no frontend change at all.
// nounPlural/nounCompound are OPTIONAL and absent whenever the base noun already reads correctly
// in that position — the frontend defaults them to `noun`, so an axis that needs no special form
// (and a backend that predates these fields) behaves exactly as it did before they existed.
export interface VocabularyAxis {
  axis: string;
  noun: string;
  nounPlural?: string;
  nounCompound?: string;
  values: SchemaValue[];
}

// Emitted ALONGSIDE classificationSchema, never replacing it: a bundle that predates this field
// keeps working unchanged. Null for roles with no active schema (gMSB/wMSB → legacy K-path).
// `attributes` is OPTIONAL for the same reason nounPlural/nounCompound are: a backend predating it
// simply reads undefined, and a role with no attributes declares none — an empty list and an absent
// field mean the same thing here, so no surface may treat the absence as an error state.
// `faces` (badge-faces round): family -> id -> the German word a surface shows for a backend-emitted
// id that is NOT a classification value (decision, registry_status, bucket, outcome). Optional for
// the same reason `attributes` is: a backend predating it reads undefined. A served id's own value
// is `string | null` — `null` is a real, servable decision ("do not put this on a row", §13.4), not
// a gap the type should paper over (this used to read plain `string`, which let a served null pass
// straight through resolveFace to a caller expecting text — final review finding 2). Read through
// useCategoryMeta().face (or shared.tsx's ServedFace, which renders it), never by indexing here —
// the three-state reading (word / served null / absent family or key) has one home.
export interface ClassificationVocabulary { primary: VocabularyAxis; secondary: VocabularyAxis | null; attributes?: VocabularyAttribute[]; faces?: Record<string, Record<string, string | null>>; }

// Sender-identity registry (P2, 2026-09-13), Task R2 — the /tickets/facets response. NAMED for the
// ROUTE and for core's own name (`/tickets/facets`), not for the active list: the one endpoint
// serves the archive buckets too (final review, item 4 — `ActiveFacets`/`getActiveFacets` read as
// an active-list-only shape and sent readers looking for an archive twin that never existed). Two
// SEPARATE facet shapes because they answer two different questions:
//   - `senderOrganisations`: the ROW SET FilterPanel's „Absender" section groups by kind — a
//     SenderOrganisationRef (types.ts) plus the count, so the org identity and its match status
//     (the 'proposed' review state) ride the same object the filter groups by.
//   - `attributes`: one entry per SERVED attribute name (TICKET_ATTRIBUTES — sparte, sender_kind),
//     each a {value,count} list — the count FilterPanel's generic attribute bubbles append. Keyed
//     by the SERVED snake_case name, same key attributeFace/attributeFilter already use, so no
//     surface re-keys a lookup on its way from the wire to a bubble.
// Every count is computed with the OTHER filters applied and the facet's OWN dimension released —
// a selected bubble never zeroes its siblings' counts (backend contract, not enforced here).
export interface SenderOrganisationFacet extends SenderOrganisationRef { count: number }
export interface AttributeFacetValue { value: string; count: number }
export interface TicketFacets {
  senderOrganisations: SenderOrganisationFacet[];
  attributes: Record<string, AttributeFacetValue[]>;
  // part 7, 2026-10-11: one {value: flag id, count} per ENFORCED flag — the „Warnhinweis" bubbles'
  // counts. Released dimension like every facet: a selected flag still shows the other flags'
  // counts. Optional on the wire: a core predating part 7 sends none, and the bubbles then read
  // a settled zero, never a crash.
  flags?: AttributeFacetValue[];
}

// part 7, 2026-10-11: the PILL filters every ticket list read takes (both list endpoints, the facets
// read and the xlsx batch selection) — „what you can recognise, you can find":
//   - `flag`: flag ids (FlagId values), OR within; sent comma-joined.
//   - `searchPrimary` / `searchSecondary`: the served primary/secondary ids whose FACE contains the
//     search term (services/searchFaceIds) — OR'd INTO the search predicate server-side (widening),
//     and ignored by core without a `search` term.
// ONE declaration for all four senders. A further pill filter is added in five places: this type,
// appendPillFilterParams below (its query spelling), searchFaceIds.pillFilterParams (page state to
// params), the `pillParams` object and dependency keys of BOTH pagination hooks
// (useActivePagination / useArchivedPagination), and AntwortenSeite's xlsx selection body.
//   - `hasForm`: part 7 fix loop, 2026-10-12 — the „Formular" pill's facet: only rows carrying a
//     recognised form (core's one FormBearing predicate). Only `true` filters; `false`/absent is
//     no filter at all, so the type admits `true` alone and the wire never carries `hasForm=false`.
export interface PillFilterParams {
  flag?: string[];
  searchPrimary?: string[];
  searchSecondary?: string[];
  hasForm?: true;
}

// The query-param spelling of PillFilterParams: comma-joined lists, omitted when absent or empty (an
// empty param would read as a filter on nothing) — the same posture as sparte/senderKind beside it.
function appendPillFilterParams(params: URLSearchParams, p: PillFilterParams): void {
  if (p.flag && p.flag.length > 0) params.append('flag', p.flag.join(','));
  if (p.searchPrimary && p.searchPrimary.length > 0) params.append('searchPrimary', p.searchPrimary.join(','));
  if (p.searchSecondary && p.searchSecondary.length > 0) params.append('searchSecondary', p.searchSecondary.join(','));
  if (p.hasForm === true) params.append('hasForm', 'true');
}

// The bucket counts each list read carries. `formBearing` = the SUBSET of each bucket whose rows carry
// a recognised form (core's one FormBearing predicate — the same rows `hasForm=true` selects), read
// by the „Formular" filter bubble as its count and, on Antworten, by the Excel batch. Optional on
// the wire: a core that does not serve it leaves the bubble with no count span, never a 0.
// part 7 fix loop, 2026-10-12: the archive buckets gained their own `formBearing` beside the
// active ones (one spelling, one shape: `counts.formBearing[<bucket>]` on both lists).
// ONE declaration per list (the pagination hooks re-export these rather than keeping their own).
export interface ActiveCounts {
  uploads: number; offen: number; entschieden: number;
  // ⚠ An UPPER BOUND on downloadable rows — the server also skips extractions it cannot render, and
  // reports the exact figure as matched-vs-delivered on the download itself.
  formBearing?: { uploads: number; offen: number; entschieden: number };
  // The server's batch cap. Read from here, NEVER mirrored as a frontend constant: two homes for one
  // number is how the lever ends up enabled at a size the server refuses.
  formBatchCap?: number;
  // Distinguishes "nothing here has a form yet" from "form recognition was never switched on for this
  // tenant" — a per-tenant flag no SQL sum can see.
  formExtractionEnabled?: boolean;
}
export interface ArchiveCounts {
  erledigt: number; liegengeblieben: number; ungeklaert: number;
  formBearing?: { erledigt: number; liegengeblieben: number; ungeklaert: number };
}

export interface AppConfig {
  chatbotEnabled: boolean;
  chatbotBaseUrl: string;
  // null ONLY on a failed /config (the catch path below) — never a fabricated role. The BE always
  // sends a role string on success; whether that string is a KNOWN role is App.tsx's call
  // (isKnownRole), not this layer's — a drifted/unrecognised string still arrives here verbatim.
  role: string | null;
  uiWarps: UiWarps;
  autoArchiveDays: number;
  classificationSchema?: ClassificationSchema | null;
  classificationVocabulary?: ClassificationVocabulary | null;
  uiRowLayout?: 'strip_label' | 'strip_abbr';
  // Eingangsweg row visibility on TicketDetail (operator_config UI_SHOW_SOURCE_CHANNEL).
  // Optional on the wire so a bundle deployed against an older backend simply reads undefined.
  uiShowSourceChannel?: boolean;
  // Demo-only: gates the "Simulierte Systemdaten" button. Mirrors the endpoint's allowlist gate
  // (true only for enerviedemo/donetzdemo) so the button never shows where the endpoint would 404.
  simulateSystemData?: boolean;
  // Tenant-wide capability gate for the form-to-Excel affordance (both list pages). Fail-closed:
  // absent on an older backend or on any /config failure means the tenant does not own the
  // capability, so the entry button never renders — distinct from the per-bucket
  // `counts.formExtractionEnabled` a ticket-list response carries, which only explains a refusal
  // once the operator is already inside excel-selection mode.
  formExtractionEnabled?: boolean;
  // Whether inline images count as attachments (operator_config UI_ATTACHMENTS_COUNT_INLINE).
  // ⚠ Absent ⇒ TRUE, the inverse of uiShowSourceChannel above: absence means "nobody has asked
  // for a change", and the unchanged behaviour is counting them.
  uiAttachmentsCountInline?: boolean;
  // The TENANT DEFAULT hidden row-signal ids (operator_config UI_ROW_PILLS_HIDDEN, a CSV on the
  // key, a list here). Every clerk who has stored no preference of their own sees this set hidden;
  // a stored preference replaces it whole (hooks/useRowPillVisibility). The ids are the receiver's
  // own registry ids (services/rowPillKinds) and the backend stores them as opaque strings.
  // THREE facts (small facts on a row, 2026-10-20): NULL = the tenant states no default and the
  // CODE default applies (DEFAULT_HIDDEN_ROW_PILLS, the kinds that ship hidden); [] = the tenant
  // states "hide nothing"; else the ids. A field absent from the payload (a backend that predates
  // the key) reads as null.
  uiRowPillsHidden: string[] | null;
  // The kinds a clerk can APPROVE on this tenant, served from the role's draft posture (core,
  // part 4b). MORE THAN ONE entry = an approval can differ from the classification, so the list
  // row states the approved handling's word; ONE entry (or an older backend serving none → [])
  // = it cannot, and the received date takes that slot. Read through useCategoryMeta().
  decisionKinds: string[];
  // The served claim-kind -> Bearbeitung handling map (core, part 4c) — e.g. wim's
  // {"reply":"antworten","forward":"weiterleiten","internal":"auskunft","ack":"zur_kenntnis",
  // "archive":"zur_kenntnis"}. `{}` on a role with no declared handling (gmsb/wmsb/vnb today) and on an
  // older backend that predates the field — same fail-open direction as decisionKinds/
  // uiRowPillsHidden above: absence means "nothing to guess a badge from", never a fabricated
  // mapping. Read through useCategoryMeta(). Consumers: shared.tsx's row-3 decision badge and
  // draftKindOrder's draft-slot ordering (services/draftClaims.ts).
  decisionKindHandling: Record<string, string>;
  // Where each flag LANDS the row, served from the role's declaration (core generation 15; part 7
  // fix loop, 2026-10-12): flag id -> { primary: the served primary id the flag lands on (null =
  // it lands nowhere of its own), implied: true when EVERY row on that primary carries the flag }.
  // The receiver reads it for one rule only — a flag the visible band already implies is not drawn
  // twice (services/impliedSignals). ⚠ Absent ⇒ {} — an older backend implies nothing, so every
  // flag stays drawn exactly as before the field existed.
  flagLandings: Record<string, FlagLanding>;
}

/** One flag's served landing (see AppConfig.flagLandings). */
export interface FlagLanding {
  primary: string | null;
  implied: boolean;
}

// Mirror of smarttix.core brand_config row (GET /brand). Colours null = auto; all set = exact override.
export interface BrandConfig {
  primaryColor: string | null;
  primaryHover: string | null;
  accentColor: string | null;
  accentHover: string | null;
  bg: string | null;
  bgSecondary: string | null;
  textColor: string | null;
  textMuted: string | null;
  border: string | null;
  font: string | null;
  textSize: string | null;
  titleSize: string | null;
  fontWeight: number | null;
  title: string;
  greeting: string;
  showBranding: boolean;
}

// Why: server-default fallback matches migration 037 defaults so a /config 4xx
// (e.g. ancient backend without uiWarps) doesn't flip historical warps off mid-deploy.
const DEFAULT_UI_WARPS: UiWarps = {
  processToAntworten: true,
  testgenProcessToAntworten: true,
  testgenUploadToUploads: true,
};

// THE fetch seam (Task C, layer b): every successful resolution writes its vocabulary/role/
// row-layout slice into the sessionStorage cache (configCache.ts) — App.tsx reads that cache
// through once at boot for an instant first paint. This function ALWAYS still hits the network on
// every call, cache or no cache: EinstellungenSeite's explicit refreshConfig() after a settings
// save (and Task B's formExtractionEnabled security gate) depend on that never being skipped — the
// cache only ever shortens the pre-paint window, never substitutes for the authoritative fetch. A
// FAILED fetch (catch path below) deliberately does NOT touch the cache: a transient outage must
// never overwrite a good cached entry with nothing.
export const getConfig = async (): Promise<AppConfig> => {
  try {
    const response = await coreHttp.get<AppConfig>('/config');
    const normalized: AppConfig = {
      ...response.data,
      uiWarps: { ...DEFAULT_UI_WARPS, ...(response.data.uiWarps ?? {}) },
      autoArchiveDays: response.data.autoArchiveDays ?? 0,
      classificationSchema: response.data.classificationSchema ?? null,
      // Absent on an older backend → null → the FE behaves exactly as it did before this field existed.
      classificationVocabulary: response.data.classificationVocabulary ?? null,
      uiRowLayout: response.data.uiRowLayout ?? undefined, // null/unset → App applies the role-aware default
      simulateSystemData: response.data.simulateSystemData ?? false,
      // ⚠ Absent ⇒ HIDDEN, in both the success and the catch path below. A backend that does not
      // know the field yet, and a /config that failed outright, mean the same thing here: nobody
      // asked for the row. Defaulting it visible would ship the state the operator's E2E rejected.
      uiShowSourceChannel: response.data.uiShowSourceChannel ?? false,
      // Same fail-closed shape: absent means the capability is not owned, never a guessed grant.
      formExtractionEnabled: response.data.formExtractionEnabled ?? false,
      // ⚠ Absent ⇒ TRUE — inverted from every other flag in this block on purpose. This is a
      // display preference, not a capability: nobody having set it means "keep counting inline
      // images", which is the behaviour every already-live tenant has today. A `false` here would
      // silently change what clerks see, so it takes the opposite fallback direction from
      // uiShowSourceChannel/formExtractionEnabled above.
      uiAttachmentsCountInline: response.data.uiAttachmentsCountInline ?? true,
      // Absent ⇒ null — the code default applies (see the field's own comment).
      uiRowPillsHidden: response.data.uiRowPillsHidden ?? null,
      // ⚠ Absent ⇒ [] — never undefined. An older backend serving no field reads exactly like a
      // tenant whose clerks can approve only one kind (see the field's own comment above).
      decisionKinds: response.data.decisionKinds ?? [],
      // ⚠ Absent ⇒ {} — never undefined, same fail-open direction as decisionKinds above. A role
      // with no declared handling and a backend that predates the field read identically.
      decisionKindHandling: response.data.decisionKindHandling ?? {},
      // ⚠ Absent ⇒ {} — nothing implied, every flag drawn (see the field's own comment).
      flagLandings: response.data.flagLandings ?? {},
    };
    writeConfigCache({
      role: normalized.role,
      classificationSchema: normalized.classificationSchema ?? null,
      classificationVocabulary: normalized.classificationVocabulary ?? null,
      uiRowLayout: normalized.uiRowLayout,
      uiAttachmentsCountInline: normalized.uiAttachmentsCountInline,
      decisionKinds: normalized.decisionKinds,
      decisionKindHandling: normalized.decisionKindHandling,
      flagLandings: normalized.flagLandings,
    });
    return normalized;
  } catch {
    // Why role: null (not a fabricated 'gmsb'): a failed /config means the FE genuinely does not
    // know the tenant's role. Silently assuming gmsb dressed a backend outage as a working gmsb
    // tenant — the caller (App.tsx) maps this to the neutral/warning UI instead.
    // uiAttachmentsCountInline: true even here — a backend outage must not look like a tenant
    // asking to stop counting inline images; see the success-path comments above for why this
    // flag inverts
    // direction from their fail-closed neighbours in this same object.
    return { chatbotEnabled: false, chatbotBaseUrl: '', role: null, uiWarps: DEFAULT_UI_WARPS, autoArchiveDays: 0, classificationSchema: null, classificationVocabulary: null, uiRowLayout: undefined, simulateSystemData: false, uiShowSourceChannel: false, formExtractionEnabled: false, uiAttachmentsCountInline: true, uiRowPillsHidden: null, decisionKinds: [], decisionKindHandling: {}, flagLandings: {} };
  }
};

// Read-through for App.tsx's instant first paint (Task C) — see configCache.ts for the full
// contract (self-consistency check, session-scoping rationale, why capability flags never ride
// along). Never touches the network; a miss (first-ever boot this tab) simply returns null and
// the caller keeps its existing neutral defaults until getConfig() resolves.
export const getCachedVocabConfig = (): CachedVocabConfig | null => readCachedVocabConfig();

// Fire-and-forget /config pre-warm (Task C, layer c) — App.tsx fires this once when the Anmeldung
// (login) screen renders, before the operator has necessarily completed sign-in. Deliberately
// bypasses coreHttp: its response interceptor above begins interactive login on ANY 401, and
// firing that from the login screen itself — before the operator has clicked anything — would be
// an unsolicited navigation on the one page whose entire job is to wait for their click. A bare
// fetch cannot trigger that interceptor. The expected pre-login 401 is handled by `r.ok` below,
// NOT by the trailing .catch(): fetch() only rejects on a genuine network-level failure (offline,
// DNS, CORS) — an HTTP error STATUS like 401 is still a resolved response, just with `ok: false`,
// so it flows into the `r.ok ? r.json() : null` branch and resolves to `null`, which the `if
// (data && …)` guard below then skips silently. .catch() exists only for that separate,
// network-level failure class.
// Returns void, never a promise the caller is expected to await or chain — this exists purely to
// warm the backend's cold start / DB connection pool before the REAL getConfig() call fires
// post-login. Feeding the vocabulary cache from a genuinely-successful response (e.g. an
// already-valid SWA session cookie riding along on this tab) is a bonus, not the point — it
// deliberately skips getConfig()'s own normalization (uiWarps merge etc.), since an
// under-normalized cache entry is corrected by the very next real getConfig() call regardless, and
// only the vocabulary-relevant slice is ever written (never formExtractionEnabled/uiWarps/etc. —
// see configCache.ts's own constraint against caching capability flags from an unauthenticated call).
export const prewarmConfig = (): void => {
  fetch(`${CORE_API_URL}/config`, { credentials: 'include' })
    .then(r => (r.ok ? r.json() : null))
    .then((data: Partial<AppConfig> | null) => {
      if (data && typeof data === 'object' && 'role' in data) {
        writeConfigCache({
          role: data.role ?? null,
          classificationSchema: data.classificationSchema ?? null,
          classificationVocabulary: data.classificationVocabulary ?? null,
          uiRowLayout: data.uiRowLayout,
        });
      }
    })
    .catch(() => { /* silent — warm-up only, see purpose above */ });
};

// GET /brand — the customer's stored brand row (Chatbot-Testseite preview). Null on any error.
export const getBrand = async (): Promise<BrandConfig | null> => {
  try {
    const response = await coreHttp.get<BrandConfig>('/brand');
    return response.data;
  } catch (err) {
    console.error('[API] Failed to get brand', err);
    return null;
  }
};

// Mirror of smarttix.core brand_detected_cache (GET /brand/detected) — the auto-detected+corrected
// palette the live widget last rendered. All colours concrete; 204/absent → null (never detected yet).
export interface DetectedBrand {
  primaryColor: string;
  primaryHover: string;
  accentColor: string;
  accentHover: string;
  bg: string;
  bgSecondary: string;
  textColor: string;
  textMuted: string;
  border: string;
  font: string;
  textSize: string | null;
  titleSize: string | null;
  fontWeight: number | null;
  detectedAt: string;
}

// GET /brand/detected — the last auto-detected palette for the Chatbot-Testseite. Null on 204
// (zero-visit), empty body, or any error. The auto card renders it inline when present.
export const getBrandDetected = async (): Promise<DetectedBrand | null> => {
  try {
    const response = await coreHttp.get<DetectedBrand | ''>('/brand/detected');
    // 204 No Content → axios yields status 204 with '' data; a real row → the object.
    if (response.status === 204 || !response.data) return null;
    return response.data as DetectedBrand;
  } catch (err) {
    console.error('[API] Failed to get detected brand', err);
    return null;
  }
};

export const getUserRoles = async (email?: string): Promise<UserRoles | null> => {
  try {
    const url = email ? `/roles?email=${encodeURIComponent(email)}` : `/roles`;
    console.log(`[API] Fetching user roles for: ${email || 'current user'} from ${url}`);
    const response = await coreHttp.get<UserRoles>(url);
    console.log('[API] User roles received:', response.data);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error(`[API] Failed to get user roles: ${err.response?.status} ${err.response?.statusText}`, err.response?.data);
    } else {
      console.error('[API] Failed to get user roles (non-axios error)', err);
    }
    return null;
  }
};

function serializeDateRanges(ranges: Array<{start: Date; end: Date}>): string {
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return ranges.map(r => `dateRanges=${fmt(r.start)}..${fmt(r.end)}`).join('&');
}

export const getStatistics = async (
  dateRanges: Array<{start: Date; end: Date}>,
  dateField?: string,
  aggregation?: string,
): Promise<TicketStatistics | null> => {
  try {
    const parts: string[] = [];
    if (dateRanges.length > 0) parts.push(serializeDateRanges(dateRanges));
    if (dateField) parts.push(`dateField=${encodeURIComponent(dateField)}`);
    if (aggregation && aggregation !== 'daily') parts.push(`aggregation=${encodeURIComponent(aggregation)}`);
    const url = parts.length > 0 ? `/statistics?${parts.join('&')}` : '/statistics';
    const response = await coreHttp.get<TicketStatistics>(url);
    return normalizeStatistics(response.data);
  } catch (err) {
    console.error('Failed to get statistics', err);
    return null;
  }
};

export const getActiveDistribution = async (): Promise<ActiveDistribution | null> => {
  try {
    const response = await coreHttp.get<ActiveDistribution>(`/tickets/active/distribution`);
    return response.data;
  } catch (err) {
    console.error('Failed to get active distribution', err);
    return null;
  }
};

// Sender-identity registry (P2, 2026-09-13), Task R2 — ONE endpoint, `/tickets/facets`, serving
// every bucket both the active AND archive lists serve (controller ruling, spec §3.5, fix round 1:
// NOT `/tickets/active/facets` — that path was this file's own guess, wrong against the actual
// contract). `bucket` is how the ONE endpoint answers for whichever list is asking, which is also
// why it stays a plain string here rather than the active-only union.
// The SAME query params as smarttix.getActivePage/getArchivedPage (see those for the
// param-building precedent this mirrors line for line: dual-sent category/primaryAxis +
// reasonCodes/secondaryAxis, comma-joined sparte/senderKind/senderOrganisationId,
// omitted-when-absent-or-empty). A facet answers "what would every OTHER value of this filter
// show", which only makes sense against the SAME filter set the list itself is reading — a facets
// call with different params from its list call would answer a question the clerk never asked.
// Returns null on any failure — the caller's job is to keep the LAST successful read rather than
// clear the panel to empty mid-fetch (UI Layout Stability; see AntwortenSeite/ArchivSeite).
export const getTicketFacets = async (p: PillFilterParams & {
  bucket?: string;
  search?: string;
  primaryAxis?: string;
  secondaryAxis?: string[];
  sparte?: string[];
  senderKind?: string[];
  senderOrganisationId?: string[];
  from?: string | null;
  to?: string | null;
  uploadedBy?: string;
}): Promise<TicketFacets | null> => {
  try {
    const params = new URLSearchParams();
    if (p.bucket) params.append('bucket', p.bucket);
    if (p.search) params.append('search', p.search);
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.sparte && p.sparte.length > 0) params.append('sparte', p.sparte.join(','));
    if (p.senderKind && p.senderKind.length > 0) params.append('senderKind', p.senderKind.join(','));
    if (p.senderOrganisationId && p.senderOrganisationId.length > 0) params.append('senderOrganisationId', p.senderOrganisationId.join(','));
    appendPillFilterParams(params, p);
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    const response = await coreHttp.get<TicketFacets>(`/tickets/facets?${params.toString()}`);
    return response.data;
  } catch (err) {
    console.error('Failed to get ticket facets', err);
    return null;
  }
};

export const getMonthlyTicketCounts = async (dateField: string): Promise<Record<string, number>> => {
  try {
    const url = `/statistics/monthly-counts?dateField=${encodeURIComponent(dateField)}`;
    const response = await coreHttp.get<Record<string, number>>(url);
    return response.data;
  } catch (err) {
    console.error('Failed to fetch monthly counts', err);
    return {};
  }
};

/** Trigger browser file download from a Blob. */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const exportStatistics = async (
  dateRanges: Array<{start: Date; end: Date}>,
  dateField: string,
  format: 'xlsx' | 'pdf',
  aggregation?: string,
  // WCAG 2.2.1 / DGUV Ch.9 — cancellable long op. BE may still finish rendering
  // the export after cancel; the FE just discards the response. Acceptable trade-off
  // (no client-state corruption; BE work is read-only).
  options?: { signal?: AbortSignal },
): Promise<void> => {
  const parts: string[] = [`dateField=${encodeURIComponent(dateField)}`, `format=${encodeURIComponent(format)}`];
  if (dateRanges.length > 0) parts.push(serializeDateRanges(dateRanges));
  if (aggregation && aggregation !== 'daily') parts.push(`aggregation=${encodeURIComponent(aggregation)}`);
  const response = await coreHttp.get(`/statistics/export?${parts.join('&')}`, {
    responseType: 'blob',
    signal: options?.signal,
  });
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const firstStart = dateRanges[0]?.start;
  const lastEnd = dateRanges[dateRanges.length - 1]?.end;
  const fromStr = firstStart ? firstStart.toISOString().slice(0, 10).replace(/-/g, '') : 'unknown';
  const toStr = lastEnd ? lastEnd.toISOString().slice(0, 10).replace(/-/g, '') : 'unknown';
  // The filename's single home is the server's Content-Disposition (deterministic per tenant —
  // the client's downstream tooling refreshes from a fixed path). The date-range name survives
  // only as the fallback for a backend that does not expose the header cross-origin yet.
  const fallback = `statistik-${fromStr}-${toStr}.${ext}`;
  const filename = filenameFromContentDisposition(response.headers['content-disposition'], fallback);
  downloadBlob(new Blob([response.data]), filename);
};

// Parses `attachment; filename="..."` (quoted or bare). Exported for its test; consumed only by
// exportStatistics above. Never throws — an unparseable header is the fallback's case.
export const filenameFromContentDisposition = (header: string | null | undefined, fallback: string): string => {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
};



// ═══════════════════════════════════════════════════════════
// Smarttix Pipeline API (cross-origin, auth via interceptor)
// ═══════════════════════════════════════════════════════════

// <purpose>The download's visible filename.</purpose>
// <constraints>Mirrors FormBatchExcelBuilder.FileNameFor on the server. NOT "aufbauliste.xlsx" — that read
// as the customer's own official sheet, which is the opposite of what this file is, and repeat downloads
// collided into "(1)". Row count so two downloads are distinguishable at a glance; timestamp
// largest-unit-first so a folder sorts chronologically on name alone. Local time on purpose: the clerk
// reads it against their own clock, not the server's.</constraints>
const notizblattFileName = (rowCount: number): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
  return `Notizblatt_${rowCount}-Zeilen_${stamp}.xlsx`;
};

export const smarttix = {
  checkHealth: async (): Promise<HealthResponse> => {
    const response = await coreHttp.get<HealthResponse>('/tickets/health');
    return response.data;
  },

  ingestTicket: async (file: File, options?: { parseOnly?: boolean; context?: string; signal?: AbortSignal }): Promise<IngestResponse> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (options?.context) formData.append('context', options.context);
    // mig 072 — this client DECLARES its own transport route rather than letting the server infer one.
    // ⚠ It is a declaration, not proof: the server normalizes it against a closed vocabulary and stores
    // NULL for anything it does not recognise, and nothing downstream may treat it as a security fact.
    // The other route ('forward', a future Outlook POST flow) declares itself the same way when it is
    // built; until then those tickets are honestly unknown rather than defaulted into a claim.
    formData.append('channel', 'upload');
    const params = options?.parseOnly ? '?parseOnly=true' : '';
    const response = await coreHttp.post<IngestResponse>(
      `/tickets/ingest${params}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // WCAG 2.2.1 / DGUV Ch.9 — long ops must be cancellable. axios respects AbortSignal.
        signal: options?.signal,
      }
    );
    return response.data;
  },

  processTicket: async (uuid: string, processContext?: ProcessContext): Promise<{ success: boolean; error?: string }> => {
    try {
      const body: Record<string, unknown> = processContext && Object.keys(processContext).length > 0 ? { ...processContext } : {};
      // Phase 4B expand window: dual-send both the old and new axis-vocabulary body key with the
      // SAME value — an old (pre-expansion) BE reads forceCategory/forceReasonCodes, an expanded
      // one can assert the two agree. Canonical source is the new-named field (ProcessContext);
      // the old key is computed FROM it, never the other way — same direction as the query-param senders above.
      if (processContext?.forcePrimaryAxis !== undefined) body.forceCategory = processContext.forcePrimaryAxis;
      if (processContext?.forceSecondaryAxis !== undefined) body.forceReasonCodes = processContext.forceSecondaryAxis;
      await coreHttp.post(`/tickets/${uuid}/process`, body);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Verarbeiten') };
    }
  },

  deleteTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Löschen') };
    }
  },

  bulkDeleteTickets: async () => {
    try {
      const response = await coreHttp.delete<{ deletedAt: string }>(`/tickets`);
      return response.data;
    } catch (error: any) {
      return { success: false as const, error: formatBackendError(error, 'Massenlöschen') };
    }
  },

  undoBulkDelete: async (deletedAt: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post('/tickets/undo-bulk-delete', { deletedAt });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig machen') };
    }
  },

  getArchivedPage: async (
    p: PillFilterParams & {
      bucket: ArchiveBucket;
      search?: string;
      primaryAxis?: string;
      secondaryAxis?: string[];
      from?: string | null;
      to?: string | null;
      uploadedBy?: string;
      // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13), comma-joined below.
      // Spelled as the CAMELCASE wire field, not as the served snake_case attribute name — the
      // translation happens once, in services/ticketAttributes.
      sparte?: string[];
      senderKind?: string[];
      // Sender-identity registry (P2, 2026-09-13), Task R2 — the organisation filter's own list
      // param, comma-joined like sparte/senderKind and NOT translated through ticketAttributes:
      // the matched organisation is a registry row, not a classified-alongside attribute.
      senderOrganisationId?: string[];
      cursor?: string | null;
      limit?: number;
    }
  ): Promise<{ rows: TicketListItem[]; nextCursor: string | null; counts: ArchiveCounts | null }> => {
    const params = new URLSearchParams({ bucket: p.bucket });
    if (p.search) params.append('search', p.search);
    // Phase 4B expand window: dual-send both the old and new axis-vocabulary param name with the
    // SAME value — an old (pre-expansion) BE reads 'category'/'reasonCodes', an expanded one can
    // assert the two agree (see normalizeTicket above for the read-side coalesce).
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    // Comma-joined like reasonCodes/secondaryAxis above, and deliberately NOT dual-sent: both names
    // are new on both sides of the wire, so there is no older spelling to keep alive. Omitted when
    // empty — an empty param would read as a filter on nothing.
    if (p.sparte && p.sparte.length > 0) params.append('sparte', p.sparte.join(','));
    if (p.senderKind && p.senderKind.length > 0) params.append('senderKind', p.senderKind.join(','));
    if (p.senderOrganisationId && p.senderOrganisationId.length > 0) params.append('senderOrganisationId', p.senderOrganisationId.join(','));
    appendPillFilterParams(params, p);
    if (p.cursor) params.append('cursor', p.cursor);
    if (p.limit) params.append('limit', p.limit.toString());
    const response = await coreHttp.get<{ rows: TicketListItem[]; nextCursor: string | null; counts: ArchiveCounts | null }>(`/tickets/archived?${params.toString()}`);
    return { ...response.data, rows: response.data.rows.map(normalizeTicket) };
  },

  getActivePage: async (
    p: PillFilterParams & {
      bucket: ActiveBucket;
      sort: 'uploaded' | 'received' | 'updated' | 'rejections' | 'zone';
      dir?: 'desc' | 'asc';
      search?: string;
      primaryAxis?: string;
      secondaryAxis?: string[];
      from?: string | null;
      to?: string | null;
      uploadedBy?: string;
      // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13), comma-joined below.
      // Spelled as the CAMELCASE wire field, not as the served snake_case attribute name — the
      // translation happens once, in services/ticketAttributes.
      sparte?: string[];
      senderKind?: string[];
      // Sender-identity registry (P2, 2026-09-13), Task R2 — see getArchivedPage's own field above.
      senderOrganisationId?: string[];
      cursor?: string | null;
      limit?: number;
    }
  ): Promise<{ rows: TicketListItem[]; nextCursor: string | null; counts: ActiveCounts | null }> => {
    const params = new URLSearchParams({ bucket: p.bucket, sort: p.sort });
    if (p.dir) params.append('dir', p.dir);
    if (p.search) params.append('search', p.search);
    // Phase 4B expand window: dual-send both the old and new axis-vocabulary param name with the
    // SAME value — an old (pre-expansion) BE reads 'category'/'reasonCodes', an expanded one can
    // assert the two agree (see normalizeTicket above for the read-side coalesce).
    if (p.primaryAxis) { params.append('category', p.primaryAxis); params.append('primaryAxis', p.primaryAxis); }
    if (p.secondaryAxis && p.secondaryAxis.length > 0) {
      const v = p.secondaryAxis.join(',');
      params.append('reasonCodes', v);
      params.append('secondaryAxis', v);
    }
    if (p.from) params.append('from', p.from);
    if (p.to) params.append('to', p.to);
    if (p.uploadedBy) params.append('uploadedBy', p.uploadedBy);
    // Comma-joined like reasonCodes/secondaryAxis above, and deliberately NOT dual-sent: both names
    // are new on both sides of the wire, so there is no older spelling to keep alive. Omitted when
    // empty — an empty param would read as a filter on nothing.
    if (p.sparte && p.sparte.length > 0) params.append('sparte', p.sparte.join(','));
    if (p.senderKind && p.senderKind.length > 0) params.append('senderKind', p.senderKind.join(','));
    if (p.senderOrganisationId && p.senderOrganisationId.length > 0) params.append('senderOrganisationId', p.senderOrganisationId.join(','));
    appendPillFilterParams(params, p);
    if (p.cursor) params.append('cursor', p.cursor);
    if (p.limit) params.append('limit', p.limit.toString());
    const response = await coreHttp.get<{ rows: TicketListItem[]; nextCursor: string | null; counts: ActiveCounts | null }>(`/tickets/active?${params.toString()}`);
    return { ...response.data, rows: response.data.rows.map(normalizeTicket) };
  },

  getTicket: async (uuid: string): Promise<TicketDetail | null> => {
    try {
      const response = await coreHttp.get<any>(`/tickets/${uuid}`);
      const detail = normalizeTicket(response.data);
      return { ...detail, suggestion: detail.suggestion ? normalizeSuggestion(detail.suggestion) : detail.suggestion };
    } catch {
      return null;
    }
  },

  // Audit one PII pill reveal click. BE writes a smarttix_log row with type+ordinal — never the value.
  // Fire-and-forget from the FE perspective; failures are swallowed (audit is defense-in-depth, not a hard gate).
  logPiiReveal: async (uuid: string, fieldType: string, index: number): Promise<void> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/pii-reveal-audit`, { fieldType, index });
    } catch { /* silent — see audit-as-defense-in-depth in pii-reveal-defensibility.md */ }
  },

  approveTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Genehmigen') };
    }
  },

  // The undo of an approval (Genehmigen settles the row, 2026-10-23): DELETE on the same resource,
  // the twin of withdrawDecline below — the row returns to Offen, approved_at and the approve claims
  // are taken back (a copy that was upgraded stays a copy). Never restricted server-side.
  withdrawApproval: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // ─── „Nicht hilfreich" (bottom-bar decision, 2026-10-16) ───
  // The third act beside approve and request-reanalysis, in the same shape: one press = the verdict
  // (the row settles into Entschieden as declined — decided, not filed; Genehmigen settles the row,
  // 2026-10-23). The receiver only PRESSES — it carries what the clerk chose in the chooser and
  // nothing else; no act logic lives here. `instead` null = „Ohne Angabe ablegen". The undo is DELETE
  // on the same resource, exactly like the copy-approve undo.
  declineTicket: async (uuid: string, instead: DeclineInstead | null): Promise<{ success: boolean; error?: string }> => {
    try {
      const body = instead === null
        ? {}
        : instead.kind === 'primary_axis'
          ? { insteadKind: 'primary_axis', insteadValue: instead.value }
          : { insteadKind: instead.kind };
      await coreHttp.post(`/tickets/${uuid}/decline`, body);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Nicht hilfreich') };
    }
  },

  withdrawDecline: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/decline`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // ─── Draft claim family (Part C, 2026-09-07) ───
  // A CLAIM records which artifact the clerk actually used. Two server answers here are NOT
  // errors and must never render as one: a refusal arrives as HTTP 200 with executed:false and
  // the server's own reason, and a stale suggestion version is that same 200 with a resultState
  // the caller has to tell apart — its remedy is "reload", not "try again".
  claimDraft: async (
    uuid: string, suggestionVersion: number, kind: DraftKind, via: ClaimVia = 'approve',
  ): Promise<TriageActionResult> => {
    try {
      const res = await coreHttp.post<{ executed: boolean; resultState: string; rejectReason?: string }>(
        TRIAGE_ACTIONS_ROUTE,
        { suggestionUuid: uuid, suggestionVersion, action: `claim_${kind}`, via });
      if (res.data.executed) return { success: true, resultState: res.data.resultState };
      return {
        success: false,
        resultState: res.data.resultState,
        error: res.data.rejectReason,
        ...(res.data.resultState === 'stale_rejected' ? { stale: true } : {}),
      };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Triage-Aktion') };
    }
  },

  // Releases the claim of ONE kind — the other kind's claim on the same row is untouched.
  // Reads the SAME outcome envelope claimDraft reads (2026-09-07), because core answers HTTP 200
  // for every undo outcome and says in the body whether it actually EXECUTED: not_undoable (the
  // claim is no longer releasable), stale_rejected (the row moved on under the clerk),
  // rejected_ineligible. Taking the 200 itself for success — which this did — told the host every
  // release had happened, so it cleared the row's copy marker and said nothing while the claim
  // demonstrably still stood. A missing/blank body is therefore NOT success either: an answer that
  // does not say it executed has not said the claim is gone.
  unclaimDraft: async (uuid: string, kind: DraftKind): Promise<TriageActionResult> => {
    try {
      const res = await coreHttp.delete<{ executed: boolean; resultState: string; rejectReason?: string }>(
        `${TRIAGE_ACTIONS_ROUTE}/${uuid}/claims/${kind}`);
      if (res.data?.executed) return { success: true, resultState: res.data.resultState };
      return {
        success: false,
        resultState: res.data?.resultState,
        error: res.data?.rejectReason,
        ...(res.data?.resultState === 'stale_rejected' ? { stale: true } : {}),
      };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Zurücknehmen') };
    }
  },

  // Composes the row's OTHER draft on demand. suggestionId is the draft's own identity, so the
  // server composes against the exact row the clerk is looking at rather than whichever is newest
  // (a 422 says the composer had nothing to produce).
  // The stale reading goes through isStaleSuggestionError, NOT through the status alone
  // (2026-09-07): this endpoint answers 409 for more than one conflict — core's „keine weiteren
  // Entwürfe" is one — and only the one carrying its stale SENTENCE means "reload". Flagging every
  // 409 stale made the host silently reload and show the reload toast for a refusal it should have
  // stated plainly.
  renderSecondaryDraft: async (
    // Every kind that HAS an artifact, the receipt included (2026-09-08) — it is composed from the
    // tenant's own receipt template through this same endpoint — and the Auskunft letter since
    // 2026-09-16 (P3 v2), which core's own kind parse takes. The union is spelled out rather
    // than imported from services/draftClaims, which imports FROM this file.
    uuid: string, suggestionId: number, kind: 'reply' | 'forward' | 'ack' | 'internal',
  ): Promise<RenderSecondaryDraftResult> => {
    try {
      const res = await coreHttp.post<{ text: string; renderSource?: string; scaffoldJson?: string | null }>(
        `/manage/suggestions/${uuid}/render-secondary`, { suggestionId, kind });
      // The answer is handed back WHOLE (2026-09-17), not just its text: the caller reveals the
      // composed card from it. Keys the server did not send are omitted rather than nulled, so a
      // reveal built on this can tell "no letter" from "a letter this core did not describe".
      return {
        success: true,
        text: res.data.text,
        ...(typeof res.data.renderSource === 'string' ? { renderSource: res.data.renderSource } : {}),
        ...(res.data.scaffoldJson !== undefined ? { scaffoldJson: res.data.scaffoldJson } : {}),
      };
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      return {
        success: false,
        error: formatBackendError(error, 'Entwurf erzeugen'),
        ...(isStaleSuggestionError(error) ? { stale: true } : {}),
        ...(status === 422 ? { refused: true } : {}),
      };
    }
  },

  // Rewrites ONE composed draft slot (Part C wave 2, 2026-09-09). The KIND names the slot in the
  // ROUTE — it is what is being written, not a parameter of the write — and suggestionId rides the
  // body as the row's identity, the same discipline renderSecondaryDraft and the segments PATCH
  // use: the server writes the exact row the clerk was reading, never whichever is newest.
  // Success is 204 No Content. There is nothing to read back and the caller REFETCHES for the new
  // text and the new stamp, so this client never invents a second source for either.
  // The errors are text/plain (core's AuthHelpers.WriteErrorAsync), not the JSON envelope the
  // segments PATCH answers with — which is exactly why the stale reading goes through
  // isStaleSuggestionError, matching the BODY SENTENCE. Three different 409s reach here and only
  // the moved-on one means "reload"; „Bereits genehmigt." (the slot is claimed — copy claims
  // included, since a copy is the indirect approval) and „Entwurf wurde noch nicht erzeugt" are
  // refusals the surface STATES. Flagging the status alone would silently reload for both and
  // swallow the sentence the clerk has to act on.
  editDraftSlot: async (
    uuid: string,
    // The kinds core's EditDraftSlot will TAKE (its own `EditableKinds`, the Auskunft letter
    // included since 2026-09-16, plus the receipt — which it refuses with a sentence of its own
    // rather than as an unknown kind).
    // `scaffoldSegments` (Task 6b) carries a SLOT FILL: the letter's segments in the served shape
    // and the served order, which the server checks against the row before rewriting the scaffold
    // and its counts (400 `segments_mismatch` otherwise). Absent for a free-text rewrite, where
    // there is no scaffold to rewrite — the key is omitted rather than sent null, so a plain
    // rewrite's request body is byte-identical to the one this client has always sent.
    body: {
      suggestionId: number;
      kind: 'reply' | 'forward' | 'ack' | 'internal';
      text: string;
      scaffoldSegments?: ScaffoldSegment[];
    },
  ): Promise<EditDraftSlotResult> => {
    try {
      await coreHttp.patch(`/manage/suggestions/${uuid}/drafts/${body.kind}`, {
        suggestionId: body.suggestionId,
        text: body.text,
        ...(body.scaffoldSegments ? { scaffoldSegments: body.scaffoldSegments } : {}),
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: formatBackendError(error, 'Speichern des Entwurfs'),
        ...(isStaleSuggestionError(error) ? { stale: true } : {}),
      };
    }
  },

  // Without responseType 'blob' axios coerces the mail bytes to a string and the saved .eml is
  // corrupt in a way that only surfaces when a mail client opens it — no error, no failed request.
  // The kinds core's .eml endpoint will serve — the three letters plus the receipt.
  downloadTriageDraft: async (
    uuid: string, type: 'reply' | 'forward' | 'ack' | 'internal',
  ): Promise<void> => {
    const response = await coreHttp.get(`${TRIAGE_ACTIONS_ROUTE}/${uuid}/draft`, {
      params: { type },
      responseType: 'blob',
    });
    downloadBlob(new Blob([response.data]), `${type}-entwurf-${uuid}.eml`);
  },

  // Copy = indirect approval (#copy-approval). Sets copy_approved_at WITHOUT
  // advancing status — the ticket stays in Offen. undoCopyApprove is the
  // write-then-revert undo (DELETE clears copy_approved_at).
  copyApproveTicket: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/copy-approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Kopieren') };
    }
  },

  // Per-ticket counterpart of downloadFormRowsXlsx. The row is assembled SERVER-side and fetched on
  // click rather than shipped on the detail DTO — the DTO carries only hasFormExtraction, so no form
  // field values reach the browser until a clerk actually asks for the row.
  // The server may REFUSE. The clipboard carries values only, while the workbook carries the notes that
  // say what was read and what disagreed — and the destination sheet is terminal, so a questionable cell
  // copied by clipboard arrives with no way to check it. A refusal is a 200 with a reason, not an error,
  // and the caller renders that reason.
  copyFormRow: async (uuid: string): Promise<{ row?: string; blocked: boolean; reason?: string }> => {
    const res = await coreHttp.post<{ row?: string; blocked?: boolean; reason?: string }>(
      `/tickets/${uuid}/form-row`, {});
    return { row: res.data.row, blocked: res.data.blocked === true, reason: res.data.reason };
  },

  undoCopyApprove: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.delete(`/tickets/${uuid}/copy-approve`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Rückgängig') };
    }
  },

  // CS-rep edit-before-approve (#5). Status must be suggestion_ready.
  // scaffoldSegments is additive (Part A (WIM two-drafts, 2026-08) Task 7): omitted for K-path/free_text (no
  // scaffold), so the PATCH body stays byte-identical to pre-Task-7 — present ⇒ the server
  // rewrites scaffold_json's segments node, so a confirmed Klärfall fill survives a reload.
  // suggestionId (fix-review round 1) rides ALONGSIDE scaffoldSegments, never alone — it is the
  // draft's own identity (ai_suggestions.id, the receiver's draftKey) so the server merges into
  // the EXACT row the fill was confirmed against, not whichever row is newest at commit time
  // (a stale client / a reanalysis race). The core handler 400s if scaffoldSegments arrives
  // without it — never falls back to "latest row" — so leaving it out here is not a safe omission.
  editSuggestion: async (
    uuid: string,
    editedText: string,
    scaffoldSegments?: ScaffoldSegment[],
    suggestionId?: number,
  ): Promise<EditSuggestionResult> => {
    try {
      await coreHttp.patch(`/tickets/${uuid}/suggestion-edit`, {
        editedText,
        ...(scaffoldSegments ? { scaffoldSegments, suggestionId } : {}),
      });
      return { success: true };
    } catch (error: any) {
      // Only the scaffoldSegments branch can answer 409 (the server merges into the exact row this
      // fill was confirmed against, and a 0-row merge is the lost race) — but the flag is set from
      // the ANSWER, not from which arguments went out, so a future 409 on the plain text path is
      // read correctly the day it exists rather than silently as a generic conflict.
      return {
        success: false,
        error: formatBackendError(error, 'Speichern der Bearbeitung'),
        ...(isStaleSuggestionError(error) ? { stale: true } : {}),
      };
    }
  },

  // Demo-only (allowlist-gated → 404 elsewhere): resolve deterministic simulated system values for
  // the ticket's empty scaffold slots. Read-only on the server; the FE draft applies + persists them
  // (single persist path). Returns per-slot {slotId,value} for useKlaerfallDraft.fillSystemSlots.
  simulateSystemData: async (uuid: string): Promise<{ success: boolean; filledSlots?: { slotId: string; value: string }[]; error?: string }> => {
    try {
      const res = await coreHttp.post(`/tickets/${uuid}/simulate-system-data`, {});
      return { success: true, filledSlots: res.data?.filledSlots ?? [] };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Simulierte Systemdaten') };
    }
  },

  requestReanalysis: async (uuid: string, body: ReanalysisRequest, options?: { signal?: AbortSignal }): Promise<{ success: boolean; error?: string }> => {
    try {
      // Phase 4B expand window: dual-send both the old and new axis-vocabulary body key with the
      // SAME value — an old (pre-expansion) BE reads suggestedCategory/forceCategory, an expanded
      // one can assert the two agree. Canonical source is the new-named field (ReanalysisRequest);
      // the old key is computed FROM it, never the other way — same direction as the query-param senders above.
      const wireBody: ReanalysisRequest & Record<string, unknown> = { ...body };
      if (body.suggestedPrimaryAxis !== undefined) wireBody.suggestedCategory = body.suggestedPrimaryAxis;
      if (body.forcePrimaryAxis !== undefined) wireBody.forceCategory = body.forcePrimaryAxis;
      await coreHttp.post(`/tickets/${uuid}/request-reanalysis`, wireBody, { signal: options?.signal });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Ablehnen') };
    }
  },

  unblockReanalysis: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/unblock-reanalysis`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Entsperren') };
    }
  },

  archiveTicket: async (uuid: string): Promise<{ success: boolean; previousStatus?: string; error?: string }> => {
    try {
      const res = await coreHttp.post(`/tickets/${uuid}/archive`);
      return { success: true, previousStatus: res.data?.previousStatus };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Archivieren') };
    }
  },

  unarchiveTicket: async (uuid: string, restoreStatus?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/unarchive`, restoreStatus ? { restoreStatus } : {});
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'Wiederherstellen') };
    }
  },

  // Auto-button bulk lifecycle (Plan #2). BE returns the count actually changed —
  // already-archived rows in the input set are silent no-ops on BulkArchive.
  // Optional AbortSignal: aborting after the HTTP request reaches BE may still let
  // the server complete the transaction — the FE just discards the response and the
  // 30s RueckgaengigToast continues to provide undo. No client-state corruption.
  // Why a sibling of bulkArchive rather than a new client: it goes through coreHttp, so it inherits the
  // request interceptor's Bearer token with no auth code of its own. A call site that carried its own
  // auth would be one the next token change could silently miss.
  // responseType 'blob' is load-bearing — without it axios coerces the workbook bytes into a JS string
  // and the downloaded file is corrupt in a way that only shows when Excel opens it.
  // Two gestures, one endpoint. `uuids` = "these, which I picked". `selection` = "everything in the
  // view I am looking at" — the server resolves that from the bucket and filters, because the client
  // can only ever enumerate the page it happens to hold.
  downloadFormRowsXlsx: async (
    target:
      | { uuids: string[] }
      // part 7, 2026-10-11 (controller ruling): the selection mirrors EVERY list filter — the
      // attribute and organisation filters and the pill filters — so a workbook never holds rows
      // the filtered screen does not. In the BODY the multi-value members are JSON arrays (core's
      // FormRowsSelection lists), not the query string's comma-joined spelling.
      | { selection: PillFilterParams & {
                       bucket: string; sort?: string; dir?: string; search?: string;
                       primaryAxis?: string; secondaryAxis?: string[];
                       sparte?: string[]; senderKind?: string[]; senderOrganisationId?: string[];
                       from?: string | null; to?: string | null; uploadedBy?: string } },
    options?: { signal?: AbortSignal }
  ): Promise<{ blob?: Blob; includedUuids: string[]; matched: number; delivered: number; fileName?: string; error?: string }> => {
    try {
      // Phase 4B expand window: the SELECTION gesture's filter fields dual-send both the old and
      // new axis-vocabulary key with the SAME value, same as the query-param senders above. The
      // uuids gesture carries no axis filter and needs no transform.
      const wireTarget = 'selection' in target
        ? {
            selection: {
              ...target.selection,
              ...(target.selection.primaryAxis !== undefined ? { category: target.selection.primaryAxis } : {}),
              ...(target.selection.secondaryAxis !== undefined ? { reasonCodes: target.selection.secondaryAxis } : {}),
            },
          }
        : target;
      const res = await coreHttp.post('/tickets/form-rows-xlsx', wireTarget, {
        responseType: 'blob',
        signal: options?.signal,
      });
      // ⚠ MATCHED and DELIVERED are different numbers on purpose. The server selects on "an extraction
      // is stored"; the workbook additionally needs that extraction to be readable. A gap means rows
      // were selected and could not be rendered — the only detector the clerk has, and it also catches
      // the view having moved between the count they saw and the click they made.
      const matched = Number(res.headers['x-smarttix-rows-matched'] ?? 0);
      const delivered = Number(res.headers['x-smarttix-rows-delivered'] ?? 0);
      // Only the picked-uuid gesture names WHICH tickets survived; the selection gesture enumerated
      // nothing client-side, so there is no list to return and none is expected.
      const included = String(res.headers['x-smarttix-included'] || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      // The visible download name is rendered HERE because an anchor's download attribute always wins
      // over Content-Disposition for a blob URL. The server composes the same convention for callers
      // that hit the endpoint directly. Named from DELIVERED rows — the selection gesture has no
      // includedUuids to count.
      // Prefer the delivered count; fall back to the included list when that header is absent. The
      // fallback is not cosmetic: the selection gesture has no included list, and the uuid gesture
      // still names the file correctly against a backend that predates these headers — or when a
      // proxy strips them. Naming a workbook "0-Zeilen" while it holds rows is the same
      // authoritative-but-wrong artifact this whole change removes.
      return { blob: res.data as Blob, includedUuids: included, matched, delivered,
               fileName: notizblattFileName(delivered || included.length) };
    } catch (error: any) {
      return { includedUuids: [], matched: 0, delivered: 0, error: formatBackendError(error, 'Excel-Download') };
    }
  },

  bulkArchive: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ archived: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ archived: number }>('/tickets/bulk-archive', { uuids }, { signal: options?.signal });
      return { archived: res.data.archived };
    } catch (error: any) {
      return { archived: 0, error: formatBackendError(error, 'Archivieren') };
    }
  },

  bulkUnarchive: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ unarchived: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ unarchived: number }>('/tickets/bulk-unarchive', { uuids }, { signal: options?.signal });
      return { unarchived: res.data.unarchived };
    } catch (error: any) {
      return { unarchived: 0, error: formatBackendError(error, 'Wiederherstellen') };
    }
  },

  bulkProcess: async (uuids: string[], options?: { signal?: AbortSignal }): Promise<{ enqueued: number; skipped: number; error?: string }> => {
    try {
      const res = await coreHttp.post<{ enqueued: number; skipped: number }>('/tickets/bulk-process', { uuids }, { signal: options?.signal });
      return { enqueued: res.data.enqueued, skipped: res.data.skipped };
    } catch (error: any) {
      return { enqueued: 0, skipped: 0, error: formatBackendError(error, 'Verarbeiten') };
    }
  },

  // The Liegengeblieben → Erledigt shortcut (Plan #2) lived here — it kept status='archived' and
  // flipped previous_status to 'approved' so the row moved to the Erledigt bucket. The capability
  // is DEACTIVATED for all roles (operator ruling, walk fix round 5, 2026-09-09) and core retires
  // /tickets/bulk-mark-erledigt in parallel, so the client is deleted rather than left standing:
  // an unused client for a route that no longer answers is a 404 with a nice German error message
  // attached, waiting for someone to find a caller for it. The archive does not approve — the route
  // is Wiederherstellen and then the ordinary approval in Offen.

  forceOcr: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await coreHttp.post(`/tickets/${uuid}/force-ocr`);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: formatBackendError(error, 'OCR-Erzwingen') };
    }
  },

  downloadDraft: async (uuid: string): Promise<void> => {
    const response = await coreHttp.get(`/tickets/${uuid}/download-draft`, {
      responseType: 'blob',
    });
    downloadBlob(new Blob([response.data]), `response-${uuid}.eml`);
  },

  // PII ignore list — UI-only suppression of detected PII entries
  getPiiIgnoreList: async (): Promise<PiiIgnoreEntry[]> => {
    try {
      const response = await coreHttp.get<any[]>(`/pii-ignore`);
      return response.data.map((r: any) => ({
        id: r.id ?? r.Id,
        fieldType: r.fieldType ?? r.FieldType ?? r.field_type,
        value: r.value ?? r.Value,
      }));
    } catch {
      return [];
    }
  },

  addPiiIgnore: async (fieldType: string, value: string): Promise<{ id: number } | null> => {
    try {
      const response = await coreHttp.post('/pii-ignore', { fieldType, value });
      return { id: response.data?.id ?? response.data?.Id };
    } catch {
      return null;
    }
  },

  removePiiIgnore: async (id: number): Promise<boolean> => {
    try {
      await coreHttp.delete(`/pii-ignore/${id}`);
      return true;
    } catch {
      return false;
    }
  },

  getTicketReads: async (): Promise<{ readUuids: string[] }> => {
    const response = await coreHttp.get<{ readUuids: string[] }>(`/tickets/reads`);
    return response.data;
  },

  markTicketRead: async (uuid: string): Promise<void> => {
    await coreHttp.post(`/tickets/read`, { uuid });
  },
};

// ─── Admin API ──────────────────────────────────────────

export const listUsers = async (): Promise<UserEntry[]> => {
  const res = await coreHttp.get<UserEntry[]>(`/manage/users`);
  return res.data;
};

export const addUser = async (email: string, isNormalUser: boolean, isCustomerAdmin: boolean): Promise<void> => {
  await coreHttp.post('/manage/users', { email, isNormalUser, isCustomerAdmin });
};

export const deleteUser = async (id: number): Promise<void> => {
  await coreHttp.delete(`/manage/users/${id}`);
};

export const updateUserRoles = async (id: number, isNormalUser: boolean, isCustomerAdmin: boolean): Promise<void> => {
  await coreHttp.put(`/manage/users/${id}/roles`, { isNormalUser, isCustomerAdmin });
};

export const getCustomerConfig = async (): Promise<Record<string, string>> => {
  const res = await coreHttp.get<Record<string, string>>(`/manage/config`);
  return res.data;
};

export const updateCustomerConfig = async (entries: Record<string, string>): Promise<void> => {
  await coreHttp.put(`/manage/config`, { entries });
};

// D3+ answer-capability catalog — read-only mirror of the vnb_acts-backed capability registry
// (Task 11 CRUD API; CRUD surface itself is a later increment, this repo's Antwort-Katalog tab
// is view-only). stance/writeOwnership are closed vocabularies from smarttix.core; typed here
// verbatim so an unrecognized future value still round-trips instead of erroring.
export interface AnswerCapabilityEntry {
  id: string;
  displayName: string;
  stance: 'vorlage' | 'grounded_rag' | 'llm_wissen';
  writeOwnership: 'code' | 'tenant';
  triggers: string[];
  active: boolean;
}

export const listAnswerCatalog = async (): Promise<AnswerCapabilityEntry[]> => {
  const res = await coreHttp.get<AnswerCapabilityEntry[]>(`/manage/answer-catalog`);
  return res.data;
};

export interface ReplyPromptEntry {
  // The overlay pipeline the row belongs to (part 5-2, 2026-10-02: the catalog serves every
  // editable pipeline — `vnb_template`, `brand`, `assessment`). Optional: an older backend served
  // the reply templates alone and sends no field, so absent reads as `vnb_template`.
  pipeline?: string;
  component: string;
  label: string;
  caution: boolean;
  effectiveText: string;
  source: 'override' | 'seed';
  seedText: string | null;
  activeRevision: number | null;
  latestRevision: number;
  // Frozen (BE-side, e.g. the two Notfall components): read-only, no override/rollback —
  // wording is compliance-reviewed and locked. Optional: older BE builds don't send it yet,
  // so absent MUST be treated as false (never assume frozen).
  frozen?: boolean;
}

export const listReplyPrompts = async (): Promise<ReplyPromptEntry[]> => {
  const res = await coreHttp.get<ReplyPromptEntry[]>(`/manage/reply-prompts`);
  return res.data;
};
export const saveReplyPrompt = async (component: string, text: string, baseRevision: number): Promise<void> => {
  await coreHttp.post(`/manage/reply-prompts`, { component, text, baseRevision });
};
export const rollbackReplyPrompt = async (component: string, revision: number): Promise<void> => {
  await coreHttp.post(`/manage/reply-prompts/${encodeURIComponent(component)}/rollback`, { revision });
};
export const resetReplyPrompt = async (component: string): Promise<void> => {
  await coreHttp.delete(`/manage/reply-prompts/${encodeURIComponent(component)}`);
};

// ── Vocabulary overlay rail (GET/POST /manage/vocabulary, DELETE /manage/vocabulary/{a}/{v}) ──
// Mirror of core VocabularyAdminFunctions' catalog records. The catalog is driven by the SEED
// vocabulary — one row per declared value, override or not — so this list is also the set of
// values an admin may edit; the rail can never add or remove one.
export interface VocabularyValueEntry {
  valueId: string;
  // The wording the tenant's seed declares, kept beside the effective one so the UI can say what
  // an edit is departing FROM without a second request.
  seedLabel: string;
  effectiveLabel: string;
  // Which layer produced effectiveLabel. 'seed' is a layer the override table cannot express
  // (absence IS seed), so the BE spells it out rather than making the FE re-derive precedence.
  source: 'seed' | 'institutional' | 'admin';
  // The value to send back as baseRevision. 0 = no admin row yet — a legitimate base, not a gap.
  adminRevision: number;
  // What a reset would REVEAL (institutional row if any, else seed) — so the confirmation can
  // name the wording that takes over instead of promising an unspecified "Standard".
  revertsTo: string;
  // The tenant's declared compact spelling for this value (part 4b) — kept beside seed the same
  // way seedLabel/effectiveLabel are, so the letters band can say what an edit departs FROM
  // without a second request. Omitted when the wire has none (an older backend, or a value with
  // no admin override and no seed abbr).
  seedAbbr?: string;
  effectiveAbbr?: string;
  // A served glyph NAME from the agreed set (HANDLING_ICONS in shared.tsx) — omitted when this
  // value serves none, which is what lets the read-only icon box render only where a tenant
  // actually declared one (uniform per tenant, never per row).
  icon?: string;
}
export interface VocabularyAxisEntry {
  axis: string;
  noun: string;
  values: VocabularyValueEntry[];
  // Letters (the compact „Kürzel" spelling) are a PRIMARY-axis fact only, fleet-wide (controller
  // ruling, final review) — core refuses a letters edit on a non-primary axis with 400 and serves
  // this flag true for exactly the primary axis. Optional/absent (an older backend, or an axis this
  // field predates) reads as `false`, NEVER `true` — the letters band/icon box/suggestion must never
  // appear on an axis this catalog hasn't explicitly cleared, since gating on the axis NAME would
  // silently disagree with core the day a role's primary axis is renamed.
  lettersEditable?: boolean;
}
// A FAMILY once grouped vocabulary values a different way than an axis does (part 4b) — a
// now-deleted "Oberfläche" card mounted OverlayRows over exactly this list. Operator ruling R36.6
// (option B, 2026-09-28) deleted that card outright: the five state-word families stay SERVED,
// never admin-editable, so this catalog carries axes only again.
export interface VocabularyCatalogResponse {
  role: string | null;
  axes: VocabularyAxisEntry[];
}

// Gen-8 tolerance (final review finding 3): a backend on the generation this endpoint predates
// serves no `axes` at all (on some fixtures) — normalized here, once, rather than at every caller,
// so `OverlayRows.flattenAxes` never has to guard against `undefined` itself. An honest empty
// list, not a crash, on an old backend. A backend older than this pass (part 4c, R36.6) may still
// send a `families` field in the wire payload — it is simply not read, no type error at runtime.
export const listVocabulary = async (): Promise<VocabularyCatalogResponse> => {
  const res = await coreHttp.get<VocabularyCatalogResponse>(`/manage/vocabulary`);
  return { ...res.data, axes: res.data.axes ?? [] };
};
// 409 (stale baseRevision) propagates to the caller exactly as saveReplyPrompt's does — the
// lost-update recovery is a reload, and only the editor knows how to preserve the rejected draft.
// `abbr` (part 4b): undefined omits the key entirely (a non-primary-axis save sends none — only
// the primary axis carries a letters band), `null` sends an explicit clear (the admin emptied the
// letters field), a string sends the current letters draft. Distinguishing undefined from null
// here is deliberate — see OverlayRows.tsx's save().
export const saveVocabularyLabel = async (
  axis: string, valueId: string, labelDe: string, baseRevision: number, abbr?: string | null,
): Promise<void> => {
  await coreHttp.post(`/manage/vocabulary`,
    { axis, valueId, labelDe, baseRevision, ...(abbr === undefined ? {} : { abbr }) });
};
export const resetVocabularyLabel = async (axis: string, valueId: string): Promise<void> => {
  await coreHttp.delete(`/manage/vocabulary/${encodeURIComponent(axis)}/${encodeURIComponent(valueId)}`);
};

export interface VnbActUpsert {
  actId: string; name: string; beschreibung: string;
  triggerBeispiele: string[]; ausschlussBeispiele: string[];
  scope: string | null; prioritaet: number;
  stance: 'llm_wissen' | 'vorlage' | 'grounded_rag';
  vorlageText: string | null; aktiv: boolean;
}
export const upsertAct = async (act: VnbActUpsert): Promise<void> => {
  await coreHttp.post(`/manage/acts`, act);
};
export const deleteAct = async (actId: string): Promise<void> => {
  await coreHttp.delete(`/manage/acts/${encodeURIComponent(actId)}`);
};

// Full per-tenant act row — camelCase mirror of core's VnbAct record (GET /manage/acts,
// ActAdminFunctions, authed non-admin read). Unlike AnswerCapabilityEntry (the read-only catalog
// DTO above), this carries every field a tenant act has, so the edit form can seed itself
// completely instead of the lossy partial seed the catalog DTO forces.
export interface VnbAct {
  id: number;
  actId: string;
  name: string;
  beschreibung: string;
  triggerBeispiele: string[];
  ausschlussBeispiele: string[];
  scope: string | null;
  prioritaet: number;
  stance: 'llm_wissen' | 'vorlage' | 'grounded_rag';
  vorlageText: string | null;
  aktiv: boolean;
  updatedAt: string | null;
}
export const listActs = async (): Promise<VnbAct[]> => {
  const res = await coreHttp.get<VnbAct[]>(`/manage/acts`);
  return res.data;
};

// ─── Routing API (routing_rules, RoutingAdminFunctions) ────────────────────────────────
// A routing rule classifies an inbound mail into a named business case and tells the clerk how to
// proceed (internalText → "Routing: an X weiterleiten"). answerDirective (optional free text) is
// handed to the answer LLM to steer/force the customer reply ("Zwischeninfo an Kunden").
export interface RoutingRule {
  id: number;
  ruleId: string;
  name: string;
  triggerBeschreibung: string;
  triggerBeispiele: string[];
  internalText: string;
  erklaerung: string | null;
  answerDirective: string | null;
  targetUnit: string | null;
  prioritaet: number;
  aktiv: boolean;
  updatedAt: string | null;
}
export interface RoutingRuleUpsert {
  ruleId: string;
  name: string;
  triggerBeschreibung: string;
  triggerBeispiele: string[];
  internalText: string;
  erklaerung: string | null;
  answerDirective: string | null;
  targetUnit: string | null;
  prioritaet: number;
  aktiv: boolean;
}
export const listRoutingRules = async (): Promise<RoutingRule[]> => {
  const res = await coreHttp.get<RoutingRule[]>(`/manage/routing`);
  return res.data;
};
export const upsertRoutingRule = async (rule: RoutingRuleUpsert): Promise<void> => {
  await coreHttp.post(`/manage/routing`, rule);
};
export const deleteRoutingRule = async (ruleId: string): Promise<void> => {
  await coreHttp.delete(`/manage/routing/${encodeURIComponent(ruleId)}`);
};

// ─── Sender-identity registry API (P2, 2026-09-13, Task R3) ────────────────────────────────
// The admin review surface for the registry R1/R2 already read from (SenderOrganisationRef,
// types.ts). A row here is the FULL record — R1/R2's ref is the trimmed read-model a ticket
// carries; this is the write-model an admin reviews/edits. `kind` is a served sender_kind
// attribute value (categoryMeta.resolveAttributeFace resolves its face — see AbsenderVerwaltung),
// never a hardcoded label here. `status` IS SenderOrganisationRef's own union, read off that type.
export interface SenderMatcher {
  type: 'domain' | 'address';
  value: string;
}
export interface SenderOrganisationRow {
  id: string;
  name: string;
  // The Kurzname an admin gives a row — DERIVED from the read-model's own field (types.ts) for the
  // same reason `status` is: one declaration, so the column the table shows and the face the ticket
  // row's pill wears cannot drift apart. null/absent = no face; the pill falls back to the name.
  shortName?: SenderOrganisationRef['shortName'];
  kind: string;
  // DERIVED, never re-spelled (final review, item 8): the review lifecycle is ONE union, owned by
  // SenderOrganisationRef (types.ts) — the read-model a ticket carries and this write-model an
  // admin reviews are the same four states, so a fifth state added there reaches this row (and
  // AbsenderVerwaltung's STATUS_DOT_COLOR map, keyed off this type and going red until it answers
  // for it — the words themselves are the served `registry_status` faces, not a local map) without
  // anyone remembering to copy it.
  status: SenderOrganisationRef['status'];
  createdBy: string | null;
  createdAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  matchers: SenderMatcher[];
  openTickets: number;
}
export interface SenderOrganisationUpsert {
  name: string;
  // Kurzname. `null` is the explicit „no face" — sent rather than omitted, because on a PATCH an
  // omitted field leaves the stored one standing, and clearing the input is a decision.
  shortName?: string | null;
  kind: string;
  matchers: SenderMatcher[];
}
export interface SenderOrganisationMergeResult {
  ticketsRepointed: number;
}
export const listSenderOrganisations = async (): Promise<SenderOrganisationRow[]> => {
  const res = await coreHttp.get<SenderOrganisationRow[]>(`/manage/senders`);
  return res.data;
};
export const createSenderOrganisation = async (payload: SenderOrganisationUpsert): Promise<SenderOrganisationRow> => {
  const res = await coreHttp.post<SenderOrganisationRow>(`/manage/senders`, payload);
  return res.data;
};
export const updateSenderOrganisation = async (id: string, payload: SenderOrganisationUpsert): Promise<SenderOrganisationRow> => {
  const res = await coreHttp.patch<SenderOrganisationRow>(`/manage/senders/${encodeURIComponent(id)}`, payload);
  return res.data;
};
export const confirmSenderOrganisation = async (id: string): Promise<void> => {
  await coreHttp.post(`/manage/senders/${encodeURIComponent(id)}/confirm`);
};
export const rejectSenderOrganisation = async (id: string): Promise<void> => {
  await coreHttp.post(`/manage/senders/${encodeURIComponent(id)}/reject`);
};
export const mergeSenderOrganisation = async (id: string, into: string): Promise<SenderOrganisationMergeResult> => {
  const res = await coreHttp.post<SenderOrganisationMergeResult>(`/manage/senders/${encodeURIComponent(id)}/merge`, { into });
  return res.data;
};
export const deleteSenderOrganisation = async (id: string): Promise<void> => {
  await coreHttp.delete(`/manage/senders/${encodeURIComponent(id)}`);
};

// ─── Row-pill preferences (P2, 2026-09-18, Task R4) ────────────────────────────────────────────
// The per-clerk „Hinweise in der Liste" store (Task C6). The ids are the RECEIVER's own registry
// ids (services/rowPillKinds) and the server holds them as opaque strings — so this pair of
// functions is a transport, never a place where a kind is interpreted.
export interface RowPillPreferences {
  /** The kind ids this clerk hides. An EMPTY list is a decision; see UiPreferences below. */
  hidden: string[];
}
export interface UiPreferences {
  /**
   * NULL means NO STORED PREFERENCE — the client reads the tenant default instead. It is NOT the
   * same as `{ hidden: [] }`, which is a clerk who deliberately hides nothing, and the two must
   * never be smoothed into one another (hooks/useRowPillVisibility carries the rule).
   */
  rowPills: RowPillPreferences | null;
}
// The one endpoint here whose failure is SWALLOWED, and deliberately: every backend on the fleet
// answers 404 until Task C6 deploys, and "no stored preference" is the honest reading of a read
// that did not land — the caller then uses the tenant default, which is what it would have done
// anyway. Its sibling PUT below takes the opposite posture on purpose.
export const getMyUiPreferences = async (): Promise<UiPreferences> => {
  try {
    const res = await coreHttp.get<UiPreferences>('/me/ui-preferences');
    // NORMALISED HERE, the same posture getConfig takes one screen up: a shape this client cannot
    // read is not a state the rest of the app should have to recognise. A 200 whose body carries no
    // `rowPills` at all (a backend that serves the envelope before it serves this preference) and a
    // `hidden` that is not a list both mean the one thing the caller can act on — NO STORED
    // PREFERENCE, i.e. use the tenant default. Anything else would hand `hidden.includes(...)` a
    // value it cannot answer for, one layer further in and with no way back to the wire.
    const stored = res.data.rowPills;
    if (!stored || !Array.isArray(stored.hidden)) return { rowPills: null };
    return { rowPills: { hidden: stored.hidden } };
  } catch {
    return { rowPills: null };
  }
};
// NO catch: a failed WRITE must reach the settings page, which has a feedback slot to say so in.
// Swallowing it would paint a save that never happened.
export const putMyRowPillPreferences = async (hidden: string[]): Promise<RowPillPreferences> => {
  const res = await coreHttp.put<RowPillPreferences>('/me/ui-preferences/row-pills', { hidden });
  return res.data;
};
// The admin „Nutzung" survey. `accounts` = clerks with a stored preference AT ALL (including one
// who hides nothing), `off` = how many of them hide that kind — so the surface computes
// on = accounts − off, and a kind nobody hides may be absent from `kinds` entirely.
export interface RowPillSurveyKind { id: string; off: number }
export interface RowPillSurvey { accounts: number; kinds: RowPillSurveyKind[] }
export const getRowPillSurvey = async (): Promise<RowPillSurvey> => {
  const res = await coreHttp.get<RowPillSurvey>('/manage/ui-preferences/row-pills/survey');
  return res.data;
};

// ─── Test Generator API ────────────────────────────────

export interface GenerateTestEmailRequest {
  role: string;
  edifact: boolean;
  targetCategory: string;
  noiseCategory?: string;
  strength: string;
  scenario?: string;
  topicCount?: number;       // 1 = Hauptthema only, 2 = + Nebenthema, 3 = + Drittes Thema
  mixedStrength?: string;    // strength for noiseCategory atom; ignored when noiseCategory unset
}

export interface GeneratedEmail {
  subject: string;
  body: string;
  emlBase64: string;
  expectedCategory: string;
  signalsUsed: string[];
}

export interface SignalAtomEntry {
  id: number;
  role: string;
  utility: string;
  channel: string;
  category: string;
  reasonCode: string;
  strength: string;
  atomText: string;
  isActive: boolean;
}

export const generateTestEmail = async (req: GenerateTestEmailRequest): Promise<GeneratedEmail> => {
  const res = await coreHttp.post<GeneratedEmail>('/tools/generate-test-email', req);
  return res.data;
};

// role is required — no default. A defaulted role here was the same silent-gmsb shape as the
// App.tsx/getConfig sites; the caller (TestgeneratorSeite) already holds the live confirmed role
// and must pass it explicitly.
export const listSignalAtoms = async (role: string): Promise<SignalAtomEntry[]> => {
  const res = await coreHttp.get<SignalAtomEntry[]>(`/tools/signal-atoms?role=${encodeURIComponent(role)}`);
  return res.data;
};

// Why: atoms are read-only for admin users. Managed via seed_signal_atoms.py → template → --db-only sync.

const emlSafeName = (subject: string): string =>
  subject.replace(/[^a-zA-Z0-9äöüÄÖÜß _-]/g, '').slice(0, 50) || 'test-email';

export const emlBase64ToFile = (emlBase64: string, subject: string): File => {
  const bytes = Uint8Array.from(atob(emlBase64), c => c.charCodeAt(0));
  return new File([bytes], `${emlSafeName(subject)}.eml`, { type: 'message/rfc822' });
};

export const downloadTestEmail = (emlBase64: string, subject: string): void => {
  const bytes = Uint8Array.from(atob(emlBase64), c => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: 'message/rfc822' });
  downloadBlob(blob, `${emlSafeName(subject)}.eml`);
};
